import { useThrottledEffect } from '@timelyapp/react';
import { produce } from 'immer';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { fromISO } from 'utils/luxon-helpers';
import type { GanttFetchState, GanttRow, GanttTasksFetchingFragment } from './gantt-types';

interface Props {
  fetchStateByRowId: Record<number, GanttFetchState>;
  visibleDays: { firstDate: DateTime<true>; lastDate: DateTime<true> };
  visibleRows: Array<GanttRow>;
  onFetchTasksAndHours: (
    fragments: Array<GanttTasksFetchingFragment>,
    resultingFetchStateByRowId: Record<number, GanttFetchState>
  ) => Promise<unknown>;
}

export const useDataFetching = ({
  fetchStateByRowId,
  visibleRows,
  visibleDays,
  onFetchTasksAndHours,
}: Props) => {
  const [isFetchingTasksAndHours, setIsFetchingTasksAndHours] = useState(false);
  useThrottledEffect(
    () => {
      if (isFetchingTasksAndHours) {
        return;
      }
      const getRowId = (row: GanttRow) => (row.type === 'user' ? row.userId : row.projectId);
      const segments = visibleRows.flatMap(
        (
          row
        ): Array<{
          startDate: string;
          endDate: string;
          id: number | null;
          type: 'user' | 'project';
        }> => {
          const fetchState = fetchStateByRowId[getRowId(row) ?? -1];
          const { firstDate, lastDate } = visibleDays;
          if (!fetchState) {
            return [
              {
                startDate: firstDate.toISODate(),
                endDate: lastDate.toISODate(),
                id: getRowId(row),
                type: row.type,
              },
            ];
          }
          const firstFetchedDate = fromISO(fetchState.firstFetchedDate);
          const lastFetchedDate = fromISO(fetchState.lastFetchedDate);
          return [
            [firstDate, firstFetchedDate],
            [lastFetchedDate, lastDate],
          ]
            .filter(
              // This filters out cases where the range is already fetched
              ([startDate, endDate]) => startDate < endDate && !startDate.hasSame(endDate, 'day')
            )
            .map(([startDate, endDate]) => ({
              startDate: startDate.toISODate(),
              endDate: endDate.toISODate(),
              id: getRowId(row),
              type: row.type,
            }));
        }
      );
      const fragmentMap = segments.reduce<Record<string, GanttTasksFetchingFragment>>(
        (map, { startDate, endDate, id, type }) => {
          (map[`${type}:${startDate}-${endDate}`] ??= {
            dates: { startDate, endDate },
            rowIds: [],
            type,
          }).rowIds.push(id);
          return map;
        },
        {}
      );
      const fragments = Object.values(fragmentMap).flat();

      if (fragments.length > 0) {
        setIsFetchingTasksAndHours(true);
        onFetchTasksAndHours(fragments, updateFetchState(fetchStateByRowId, fragments)).finally(
          () => setIsFetchingTasksAndHours(false)
        );
      }
    },
    [
      visibleRows,
      visibleDays,
      fetchStateByRowId,
      onFetchTasksAndHours,
      isFetchingTasksAndHours,
      setIsFetchingTasksAndHours,
    ],
    500
  );
  return { isFetchingTasksAndHours };
};

export const updateFetchState = (
  fetchStateByRowId: Record<number, GanttFetchState>,
  fragments: Array<GanttTasksFetchingFragment>
): Record<number, GanttFetchState> => {
  return produce(fetchStateByRowId, fetchStateByRowIdDraft => {
    fragments.forEach(({ rowIds, dates }) => {
      const rowIdsSet = new Set(rowIds);
      const startDate = fromISO(dates.startDate);
      const endDate = fromISO(dates.endDate);
      rowIds.forEach(rowId => {
        if (!rowIdsSet.has(rowId)) {
          return;
        }
        fetchStateByRowIdDraft[rowId ?? -1] = {
          firstFetchedDate: (
            DateTime.min(
              startDate,
              fromISO(fetchStateByRowIdDraft[rowId ?? -1]?.firstFetchedDate ?? dates.startDate)
            ) as DateTime<true>
          ).toISODate(),
          lastFetchedDate: (
            DateTime.max(
              endDate,
              fromISO(fetchStateByRowIdDraft[rowId ?? -1]?.lastFetchedDate ?? dates.endDate)
            ) as DateTime<true>
          ).toISODate(),
        };
      });
    });
  });
};
